import { LitElement, html } from 'lit';
import { customElement, state, property } from 'lit/decorators.js';
import { styles } from './assets-page.style.js';
import { getAssetsByCompany, deleteAsset, getLocationsByCompany } from '@cmms/api';
import { Firestore } from 'firebase/firestore';
import { createAssetsTableConfig } from './assets-table-config.js';


interface Asset {
  id: string;
  asset_id?: string;
  name: string;
  parent_id?: string;
  parent_name?: string | null;
  status: string;
  company_id?: string;
  photo_url?: string | null;
  qr_code_url?: string | null;
  created_at?: number;
  updated_at?: number;
  location_id?: string;
  cost?: number;
  purchase_date?: number;
  warranty_date?: number;
  fields?: Array<{ name: string; type: string; value: string }>;
  model?: any;
}
interface LocationRow { id: string; altId?: string; name: string; }

@customElement('assets-page')
export class AssetsPage extends LitElement {
  @property({ type: String }) companyId: string = '';
  @property({ type: Object }) firestore: Firestore = null as any;

  @state() private assets: Asset[] = [];
  @state() private locations: LocationRow[] = [];
  @state() private locationIndex: Map<string, string> = new Map();

  @state() private isLoading = true;
  @state() private error: string | null = null;
  @state() private showAddModal = false;
  @state() private selectedAsset: Asset | null = null;
  @state() private showDeleteModal = false;
  @state() private assetToDelete: Asset | null = null;

  connectedCallback() {
    super.connectedCallback();
    this.loadData();
  }

  private asArray<T = any>(resp: unknown): T[] {
    if (Array.isArray(resp)) return resp as T[];
    // @ts-ignore
    if (resp && Array.isArray((resp as any).data)) return (resp as any).data as T[];
    return [];
  }

  private async loadData() {
    try {
      this.isLoading = true;
      this.error = null;

      const [assetsResp, locationsResp] = await Promise.all([
        getAssetsByCompany(this.firestore, this.companyId),
        getLocationsByCompany(this.firestore, this.companyId)
      ]);

      const assetsRaw = this.asArray<Asset>(assetsResp);
      const locationsRaw = this.asArray<any>(locationsResp);

      // Normalize assets
      let assets = (assetsRaw || []).map((a: any) => {
        const m = a.model || {};
        return {
          ...a,
          id: a.id || a.asset_id || m.asset_id || crypto.randomUUID(),
          asset_id: a.asset_id || m.asset_id,
          name: a.name || m.name || 'Unnamed',
          status: a.status || m.status || 'Active',
          location_id: a.location_id || m.location_id,
          parent_id: a.parent_id || m.parent_id,
          parent_name: a.parent_name || m.parent_name || null,
          photo_url: a.photo_url || m.photo_url || null,
          qr_code_url: a.qr_code_url || m.qr_code_url || null,
          created_at: a.created_at ?? m.created_at,
          updated_at: a.updated_at ?? m.updated_at,
          purchase_date: a.purchase_date ?? m.purchase_date,
          warranty_date: a.warranty_date ?? m.warranty_date,
          cost: a.cost ?? m.cost
        } as Asset;
      });

      // Build locations index
      const normalized: LocationRow[] = (locationsRaw || []).map((l: any) => ({
        id: l.id || l.$id || l.location_id,
        altId: l.location_id || l.model?.location_id || undefined,
        name: l.name || l.model?.name || 'Unknown'
      }));
      this.locations = normalized;
      const idx = new Map<string, string>();
      for (const l of normalized) {
        if (l.id) idx.set(String(l.id), l.name);
        if (l.altId) idx.set(String(l.altId), l.name);
      }
      this.locationIndex = idx;

      this.assets = assets;
    } catch (e: any) {
      this.error = e?.message ?? 'Failed to load assets';
    } finally {
      this.isLoading = false;
    }
  }

  private handleAddAsset = () => { 
    this.selectedAsset = null; 
    this.showAddModal = true; 
  };

  private handleRowOpen = (asset: Asset) => {
    this.selectedAsset = asset;
    const viewer = document.createElement('asset-viewer') as any;
    viewer.open = true;
    viewer.asset = asset;
    viewer.companyId = this.companyId;
    viewer.assetId = asset.id;
    document.body.appendChild(viewer);
    viewer.addEventListener('asset-updated', () => this.loadData(), { once: true });
    viewer.addEventListener('close', () => this.loadData(), { once: true });
  };

  private handleDelete = (asset: Asset) => { 
    this.assetToDelete = asset; 
    this.showDeleteModal = true; 
  };
  private handleDeleteCancel = () => { 
    this.showDeleteModal = false; 
    this.assetToDelete = null; 
  };
  private handleDeleteConfirm = async () => {
    if (!this.assetToDelete) return;
    try {
      this.isLoading = true;
      await deleteAsset(this.firestore, this.assetToDelete.id);
      this.assets = (this.assets || []).filter(a => a.id !== this.assetToDelete!.id);
      this.showDeleteModal = false;
      this.assetToDelete = null;
    } catch (e: any) {
      this.error = e?.message ?? 'Failed to delete asset';
    } finally {
      this.isLoading = false;
    }
  };

  private renderDeleteDialog() {
    return html`
      <sl-dialog ?open=${this.showDeleteModal} label="Delete Asset" @sl-after-hide=${this.handleDeleteCancel}>
        <div class="modal-body">
          <sl-alert variant="danger" open>
            <sl-icon slot="icon" name="exclamation-octagon"></sl-icon>
            This action cannot be undone.
          </sl-alert>
          <p>Are you sure you want to delete <strong>${this.assetToDelete?.name ?? 'this asset'}</strong>?</p>
        </div>
        <div slot="footer" class="modal-footer">
          <sl-button @click=${this.handleDeleteCancel} ?disabled=${this.isLoading}>Cancel</sl-button>
          <sl-button variant="danger" @click=${this.handleDeleteConfirm} ?loading=${this.isLoading} ?disabled=${this.isLoading}>
            ${this.isLoading ? 'Deleting…' : 'Delete'}
          </sl-button>
        </div>
      </sl-dialog>
    `;
  }

  render() {
    const tableConfig = createAssetsTableConfig(
      this.assets,
      this.locations,
      this.locationIndex,
      {
        onRowClick: this.handleRowOpen,
        onDelete: this.handleDelete,
        onAddAsset: this.handleAddAsset
      }
    );

    // Override loading and error state
    tableConfig.loading = this.isLoading;
    tableConfig.error = this.error;

    return html`
      <div class="assets-page">
        <data-table .config=${tableConfig}></data-table>

        <asset-modal
          .open=${this.showAddModal}
          .asset=${this.selectedAsset}
          .companyId=${this.companyId}
          .firestore=${this.firestore}
          @close=${() => { this.showAddModal = false; this.selectedAsset = null; }}
          @saved=${() => { this.showAddModal = false; this.selectedAsset = null; this.loadData(); }}>
        </asset-modal>

        ${this.renderDeleteDialog()}
      </div>
    `;
  }

  static styles = styles;
}
